"use client";

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PATHS, { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths";
import useUserStore from "@/features/auth/store/useUserStore";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale";
import GenericLoader from "@/components/GenericLoader.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import EditPaymentDialog from "@/features/finances/components/EditPaymentDialog";
import NotifyPaymentDialog from "@/features/finances/components/NotifyPaymentDialog";
import ConfirmationReviewDialog from "@/features/finances/components/ConfirmationReviewDialog";
import DeletePaymentDialog from "@/features/finances/components/DeletePaymentDialog";
import RestorePaymentDialog from "@/features/finances/components/RestorePaymentDialog";

type PaymentType = "credits" | "lesson" | "other" | string;

interface PaymentStatus {
  code: string;
  group: "completed" | "failed" | "pending" | "unknown" | string;
  label: string;
  icon?: string | null;
  color?: string | null;
}

interface PaymentPayer {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  full_name: string;
}

interface PaymentLessonDetails {
  lesson_id: string;
  workspace_id: string;
  topic: string;
  start_time_formatted?: string;
  start_time?: string;
  subject?: { id: string; name: string; icon?: string } | null;
  amount?: string; // formatted
}

interface PaymentCreditsDetails {
  id: string;
  credits: number;
  price_per_credit: string; // formatted
  total: string; // formatted
}

interface PaymentResource {
  id: string;
  type: PaymentType;
  direction: string; // "pay-in" | "payment"
  status: PaymentStatus;
  amount: string; // formatted
  original_amount_value?: number | null;
  original_currency?: string | null;
  converted_amount?: string | null;
  conversion_rate?: number | null;
  conversion_currency?: string | null;
  conversion_rate_date?: string | null;
  transaction_type?: string | null;
  payment_due?: string | null;
  description?: string | null;
  payer: PaymentPayer | null;
  lesson_details: PaymentLessonDetails | null;
  credits_details: PaymentCreditsDetails | null;
  archived?: boolean;
  archived_at?: string | null;
  created_at?: string | null;
  processed_at?: string | null;
  confirmations?: PaymentConfirmation[] | null;
}

interface PaymentConfirmation {
  id: string;
  evidence_url?: string | null;
  description?: string | null;
  status: string;
  review_comment?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface PaymentHistoryEntry {
  id: string;
  action: string;
  message: string;
  meta?: Record<string, unknown> | null;
  initiator?: PaymentPayer | null;
  created_at: string; // localized
}

interface PaymentDetailsResponse {
  data: {
    payment: PaymentResource;
    history: PaymentHistoryEntry[];
    notifications?: {
      last_sent_at?: string | null;
      next_available_at?: string | null;
      can_send_now?: boolean;
    } | null;
  };
}

export default function PaymentDetailsPage() {
  const { t } = useTranslation(["finances", "common"]);
  const { id } = useParams<{ id: string }>();
  const [details, setDetails] = useState<PaymentDetailsResponse["data"] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const { user } = useUserStore();
  const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const [selectedConfirmationId, setSelectedConfirmationId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState<boolean>(false);

  const fetchDetails = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError("");
      const { data } = await axiosApiInstance.get<PaymentDetailsResponse>(
        API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_DETAILS(id),
      );

      setDetails(data.data);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("finances:paymentsDetails.errors.fetchFailed", "Failed to fetch payment details"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const statusColorStyle = (color?: string | null) => ({
    color: color || "var(--muted-foreground)",
  });

  if (isLoading) {
    return <GenericLoader />;
  }

  if (error || !details) {
    return (
      <>
        <title>{t("finances:paymentsDetails.pageTitle", "Payment Details - Ximly")}</title>
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="relative mx-auto max-w-4xl p-4 lg:p-6">
            <motion.div
              animate={{ opacity: 0.2 }}
              className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 opacity-30 transition-colors duration-700"
              initial={{ opacity: 0 }}
            />
            <motion.div
              animate={{ opacity: 0.2 }}
              className="absolute top-25 -right-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
              initial={{ opacity: 0.2 }}
            />

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="mb-6 flex items-center gap-4">
                <Button asChild size="sm" variant="ghost">
                  <Link to={PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY}>
                    <Icon height="1rem" icon="tabler:arrow-left" width="1rem" />
                    {t("finances:paymentsDetails.backToPayments", "Back to Payments")}
                  </Link>
                </Button>
              </div>

              <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-none p-8 shadow-2xl backdrop-blur-xl">
                <div className="relative text-center">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                    <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
                  </div>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
                    <Icon
                      className="text-red-500"
                      height="2rem"
                      icon="tabler:alert-circle"
                      width="2rem"
                    />
                  </div>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("finances:paymentsDetails.error.title", "Error")}
                  </h1>
                  <p className="text-muted-foreground mt-2 text-lg">{error}</p>
                </div>
              </Card>
            </motion.div>
          </div>
        </ScrollArea>
      </>
    );
  }

  const { payment, history } = details;
  const canResolve = payment.status?.code === "waiting_for_confirmation";
  const isArchived = !!payment.archived;
  const hasPendingConfirmation = (payment.confirmations || []).some(
    (c) => c.status === "submitted",
  );

  return (
    <>
      <title>{t("finances:paymentsDetails.pageTitle", "Payment Details - Ximly")}</title>
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="relative mx-auto max-w-4xl p-4 lg:p-6">
          {/* Background circles */}
          <motion.div
            animate={{ opacity: 0.35 }}
            className="absolute -bottom-48 -left-48 size-120 rounded-full transition-colors duration-700"
            initial={{ opacity: 0 }}
            style={{
              background: isArchived
                ? "linear-gradient(to bottom right, rgba(156, 163, 175, 0.25), rgba(107, 114, 128, 0.25))" // gray gradient for archived
                : "linear-gradient(to bottom right, rgba(99, 102, 241, 0.25), rgba(147, 51, 234, 0.25))", // indigo-purple for active
            }}
          />
          <motion.div
            animate={{ opacity: 0.35 }}
            className="absolute top-28 -right-48 size-120 rounded-full transition-colors duration-700"
            initial={{ opacity: 0.2 }}
            style={{
              background: isArchived
                ? "linear-gradient(to bottom right, rgba(156, 163, 175, 0.25), rgba(107, 114, 128, 0.25))" // gray gradient for archived
                : "linear-gradient(to bottom right, rgba(20, 184, 166, 0.25), rgba(6, 182, 212, 0.25))", // teal-cyan for active
            }}
          />

          {/* Header */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <div className="mb-6 flex items-center justify-between gap-3">
              <Button asChild size="sm" variant="ghost">
                <Link to={PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY}>
                  <Icon height="1rem" icon="tabler:arrow-left" width="1rem" />
                  {t("finances:paymentsDetails.backToPayments", "Back to Payments")}
                </Link>
              </Button>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Button
                        className="h-8 w-8 p-0"
                        size="sm"
                        variant="foreground"
                        onClick={() => setIsEditDialogOpen(true)}
                      >
                        <Icon height="1rem" icon="mdi:pencil" width="1rem" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {t("finances:paymentsEdit.open", "Edit this payment")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {!isArchived && (
                  <TooltipProvider>
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            className="h-8 w-8 p-0"
                            size="sm"
                            variant="destructive"
                            onClick={() => setIsDeleteDialogOpen(true)}
                          >
                            <Icon height="1rem" icon="mdi:trash-can" width="1rem" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {t(
                          "finances:paymentsDetails.deleteRestore.deleteTooltip",
                          "Delete payment",
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {isArchived && (
                  <TooltipProvider>
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            className="h-8 w-8 p-0"
                            size="sm"
                            variant="ghost"
                            onClick={() => setIsRestoreDialogOpen(true)}
                          >
                            <Icon height="1rem" icon="mdi:backup-restore" width="1rem" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {t(
                          "finances:paymentsDetails.deleteRestore.restoreTooltip",
                          "Restore payment",
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>

            <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-none p-8 shadow-2xl backdrop-blur-xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
              </div>

              <div className="relative text-center">
                <div
                  className={`bg-muted/20 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full`}
                  style={{
                    backgroundColor: payment.status?.color
                      ? `color-mix(in oklab, ${payment.status.color} 20%, transparent)`
                      : undefined,
                  }}
                >
                  {payment.status?.icon ? (
                    <Icon
                      height="2rem"
                      icon={payment.status.icon}
                      style={statusColorStyle(payment.status?.color)}
                      width="2rem"
                    />
                  ) : (
                    <Icon
                      height="2rem"
                      icon="tabler:info-circle"
                      style={statusColorStyle(payment.status?.color)}
                      width="2rem"
                    />
                  )}
                </div>
                <h1 className="text-accent-foreground text-3xl font-bold md:text-4xl">
                  {t("finances:paymentsDetails.title", "Payment Details")}
                </h1>
                <p className="text-muted-foreground mt-2 text-lg">
                  <span
                    className="font-medium capitalize"
                    style={statusColorStyle(payment.status?.color)}
                  >
                    {payment.status?.label}
                  </span>
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Pending confirmation CTA (below header) */}
          {canResolve && hasPendingConfirmation && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
              initial={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl p-4 shadow-2xl backdrop-blur-xl">
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute -top-16 -left-16 h-36 w-36 rounded-full bg-gradient-to-br from-amber-400 to-orange-500" />
                </div>
                <div className="relative flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-lg">
                      <Icon
                        className="text-accent-foreground"
                        height="1.1rem"
                        icon="mdi:clock-outline"
                        width="1.1rem"
                      />
                    </div>
                    <div>
                      <div className="text-accent-foreground font-semibold">
                        {t(
                          "finances:paymentsDetails.confirmations.pendingTitle",
                          "Confirmation pending",
                        )}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        {t(
                          "finances:paymentsDetails.confirmations.pendingDesc",
                          "A payment confirmation was submitted and is awaiting your review.",
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    className="w-full sm:w-auto"
                    size="sm"
                    variant="foreground"
                    onClick={() => {
                      const firstPending =
                        payment.confirmations?.find((c) => c.status === "submitted") ||
                        payment.confirmations?.[0] ||
                        null;

                      setSelectedConfirmationId(firstPending?.id || null);
                      setIsConfirmationDialogOpen(true);
                    }}
                  >
                    <Icon height="1rem" icon="mdi:eye" width="1rem" />
                    {t("finances:paymentsDetails.confirmations.reviewCta", "Review confirmation")}
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Summary */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-emerald-400 to-teal-600" />
                <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-purple-400 to-blue-500" />
              </div>
              <div className="relative space-y-6">
                {/* Archived Payment Card */}
                {isArchived && (
                  <div className="bg-muted/20 rounded-lg p-6 text-center">
                    <div className="mb-3 flex justify-center">
                      <div className="bg-muted/40 flex h-16 w-16 items-center justify-center rounded-lg">
                        <Icon
                          className="text-muted-foreground"
                          height="2rem"
                          icon="mdi:archive"
                          width="2rem"
                        />
                      </div>
                    </div>
                    <h3 className="text-accent-foreground mb-2 text-xl font-semibold">
                      {t("finances:paymentsDetails.archived.title", "Archived Payment")}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {t(
                        "finances:paymentsDetails.archived.message",
                        "This payment was archived on",
                      )}{" "}
                      {payment.archived_at &&
                        format(new Date(payment.archived_at), "PPP", {
                          locale: currentLanguageLocale(),
                        })}
                    </p>
                  </div>
                )}

                <div
                  className={`grid grid-cols-1 gap-4 ${payment.payment_due ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
                >
                  {/* Amount */}
                  <div className="bg-muted/20 rounded-lg p-4 text-center">
                    <div className="mb-2 flex justify-center">
                      <div className="bg-muted/30 flex h-12 w-12 items-center justify-center rounded-lg">
                        <Icon
                          className="text-accent-foreground"
                          height="1.5rem"
                          icon="tabler:cash"
                          width="1.5rem"
                        />
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm font-medium">
                      {t("finances:checkout.summary.totalAmount", "Total Amount")}
                    </p>
                    <div className="text-accent-foreground text-2xl font-bold">
                      {payment.conversion_rate && payment.conversion_rate !== 1 ? (
                        <TooltipProvider>
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <span>{payment.converted_amount || payment.amount}</span>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <span>
                                {t(
                                  "finances:paymentsHistory.conversionTooltip",
                                  "Converted from {{originalAmount}} at a rate of {{rate}} on {{date}}",
                                  {
                                    originalAmount:
                                      `${payment.original_amount_value ?? ""} ${payment.original_currency ?? ""}`.trim(),
                                    rate: String(payment.conversion_rate),
                                    date:
                                      (payment.conversion_rate_date
                                        ? format(new Date(payment.conversion_rate_date), "PPP", {
                                            locale: currentLanguageLocale(),
                                          })
                                        : "") || "",
                                  },
                                )}
                              </span>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        payment.amount
                      )}
                    </div>
                    {payment.conversion_rate && payment.conversion_rate !== 1 && (
                      <div className="text-muted-foreground mt-1 text-xs font-normal">
                        {`${payment.original_amount_value ?? ""} ${payment.original_currency ?? ""}`.trim()}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="bg-muted/20 rounded-lg p-4 text-center">
                    <div className="mb-2 flex justify-center">
                      <div className="bg-muted/30 flex h-12 w-12 items-center justify-center rounded-lg">
                        <Icon
                          className="text-accent-foreground"
                          height="1.5rem"
                          icon="tabler:status-change"
                          width="1.5rem"
                        />
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm font-medium">
                      {t("finances:checkout.details.paymentStatus", "Payment Status")}
                    </p>
                    <p
                      className="text-2xl font-bold capitalize"
                      style={statusColorStyle(payment.status?.color)}
                    >
                      {payment.status?.label}
                    </p>
                  </div>

                  {/* Payment due (if any) */}
                  {payment.payment_due && (
                    <div className="bg-muted/20 rounded-lg p-4 text-center">
                      <div className="mb-2 flex justify-center">
                        <div className="bg-muted/30 flex h-12 w-12 items-center justify-center rounded-lg">
                          <Icon
                            className="text-accent-foreground"
                            height="1.5rem"
                            icon="tabler:calendar-due"
                            width="1.5rem"
                          />
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("finances:paymentsHistory.paymentDue", "Payment due")}
                      </p>
                      <p className="text-accent-foreground text-2xl font-bold">
                        {format(new Date(payment.payment_due), "PPP", {
                          locale: currentLanguageLocale(),
                        })}
                      </p>
                    </div>
                  )}
                </div>

                {/* Accept/Reject actions moved into confirmation dialog */}
              </div>
            </Card>
          </motion.div>

          {/* Details */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-cyan-600" />
                <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-yellow-500" />
              </div>
              <div className="relative space-y-6">
                <h3 className="text-accent-foreground text-lg font-semibold">
                  {t("finances:paymentsDetails.detailsTitle", "Details")}
                </h3>

                {payment.payer && (
                  <div className="group bg-muted/20 relative overflow-hidden rounded-lg p-4">
                    <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-25">
                      <img
                        alt={payment.payer.full_name}
                        className="h-full w-full object-cover blur-2xl"
                        src={payment.payer.avatar_url || "/placeholder.svg?height=40&width=40"}
                      />
                    </div>
                    <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage
                            alt={payment.payer.full_name}
                            src={payment.payer.avatar_url || "/placeholder.svg?height=40&width=40"}
                          />
                          <AvatarFallback>
                            {payment.payer.first_name?.[0]}
                            {payment.payer.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-accent-foreground truncate text-sm font-medium">
                              {payment.payer.full_name}
                            </h4>
                          </div>
                        </div>
                      </div>
                      {details.notifications && (
                        <div className="flex flex-col items-stretch sm:ml-auto sm:items-end">
                          <TooltipProvider>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <span className="inline-flex w-full sm:w-auto">
                                  <Button
                                    className="h-8 w-full justify-center gap-2 px-3 text-xs sm:w-auto"
                                    disabled={!details.notifications.can_send_now}
                                    size="sm"
                                    variant="foreground"
                                    onClick={() => setIsNotifyDialogOpen(true)}
                                  >
                                    <Icon height="1rem" icon="mdi:bell-ring" width="1rem" />
                                    {t("finances:paymentsDetails.sendReminder", "Send reminder")}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!details.notifications.can_send_now &&
                                details.notifications.next_available_at && (
                                  <TooltipContent side="left">
                                    <div className="flex items-center gap-2">
                                      <Icon height="1rem" icon="mdi:clock-outline" width="1rem" />
                                      <span className="text-sm">
                                        {t(
                                          "finances:paymentsDetails.nextAvailableAt",
                                          "Next available at",
                                        )}{" "}
                                        {format(
                                          new Date(details.notifications.next_available_at),
                                          "PPPp",
                                          {
                                            locale: currentLanguageLocale(),
                                          },
                                        )}
                                      </span>
                                    </div>
                                  </TooltipContent>
                                )}
                            </Tooltip>
                          </TooltipProvider>
                          {details.notifications.last_sent_at && (
                            <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                              <Icon height="0.875rem" icon="mdi:clock-outline" width="0.875rem" />
                              <span>
                                {t("finances:paymentsDetails.lastSentAt", "Last sent at")}:{" "}
                                {format(new Date(details.notifications.last_sent_at), "PPPp", {
                                  locale: currentLanguageLocale(),
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {payment.lesson_details && (
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <Icon
                          className="text-accent-foreground"
                          height="1.1rem"
                          icon="mdi:calendar-clock"
                          width="1.1rem"
                        />
                        <h4 className="text-accent-foreground font-semibold">
                          {t("finances:paymentsDetails.lessonDetailsTitle", "Lesson Details")}
                        </h4>
                      </div>
                      <div className="sm:hidden">
                        {(() => {
                          const workspaceId = payment.lesson_details?.workspace_id;
                          const lessonId = payment.lesson_details?.lesson_id;

                          if (!workspaceId || !lessonId) return null;
                          const basePath =
                            user?.role === "student"
                              ? STUDENT_WORKSPACE_PATHS(workspaceId).LESSON_DETAILS
                              : TUTOR_WORKSPACE_PATHS(workspaceId).LESSON_DETAILS;

                          return (
                            <Button asChild className="w-full" size="sm" variant="foreground">
                              <Link to={`${basePath}/${lessonId}/details`}>
                                <Icon
                                  className="mr-1"
                                  height="1rem"
                                  icon="mdi:arrow-right"
                                  width="1rem"
                                />
                                {t("common:calendar.slot.viewLessonDetails", "View lesson details")}
                              </Link>
                            </Button>
                          );
                        })()}
                      </div>
                      <div className="hidden sm:block">
                        {(() => {
                          const workspaceId = payment.lesson_details?.workspace_id;
                          const lessonId = payment.lesson_details?.lesson_id;

                          if (!workspaceId || !lessonId) return null;
                          const basePath =
                            user?.role === "student"
                              ? STUDENT_WORKSPACE_PATHS(workspaceId).LESSON_DETAILS
                              : TUTOR_WORKSPACE_PATHS(workspaceId).LESSON_DETAILS;

                          return (
                            <Button asChild size="sm" variant="foreground">
                              <Link to={`${basePath}/${lessonId}/details`}>
                                <Icon className="mr-1" icon="mdi:arrow-right" width="1rem" />
                                {t("common:calendar.slot.viewLessonDetails", "View lesson details")}
                              </Link>
                            </Button>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="mb-2 flex flex-col gap-2 text-xs sm:mb-3 sm:flex-row sm:items-center sm:text-sm">
                      {payment.lesson_details.subject?.icon &&
                        payment.lesson_details.subject?.name && (
                          <div className="flex items-center gap-2">
                            <Icon
                              className="text-muted-foreground"
                              height="1rem"
                              icon={payment.lesson_details.subject.icon}
                              width="1rem"
                            />
                            <span className="text-muted-foreground">
                              {payment.lesson_details.subject.name}
                            </span>
                          </div>
                        )}
                      {payment.lesson_details.topic && (
                        <>
                          <span className="text-muted-foreground mx-1 hidden sm:inline">•</span>
                          <span className="text-accent-foreground font-medium">
                            {payment.lesson_details.topic}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="text-muted-foreground flex flex-col gap-2 sm:flex-row sm:text-sm">
                      {payment.lesson_details.start_time && (
                        <div className="flex w-fit items-center gap-2">
                          <Icon
                            className="text-muted-foreground"
                            height="1rem"
                            icon="mdi:calendar-range"
                            width="1rem"
                          />
                          <span>
                            {format(new Date(payment.lesson_details.start_time), "PPPp", {
                              locale: currentLanguageLocale(),
                            })}
                          </span>
                        </div>
                      )}
                      <span className="hidden sm:inline">•</span>
                      {payment.lesson_details.amount && (
                        <div className="flex items-center gap-1">
                          <Icon
                            className="text-muted-foreground"
                            height="1rem"
                            icon="mdi:cash"
                            width="1rem"
                          />
                          <span>{payment.lesson_details.amount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {payment.credits_details && (
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground"
                        height="1.1rem"
                        icon="tabler:coins"
                        width="1.1rem"
                      />
                      <h4 className="text-accent-foreground font-semibold">
                        {t("finances:paymentsDetails.creditsDetailsTitle", "Credits Purchase")}
                      </h4>
                    </div>
                    <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
                      <span>
                        {payment.credits_details.credits}{" "}
                        {t("finances:buyCredits.credits", "credits")}
                      </span>
                      <span>
                        • {t("finances:buyCredits.perCredit", "per credit")}{" "}
                        {payment.credits_details.price_per_credit}
                      </span>
                      <span>
                        • {t("finances:checkout.summary.totalAmount", "Total Amount")}{" "}
                        {payment.credits_details.total}
                      </span>
                    </div>
                  </div>
                )}

                {payment.description && (
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground"
                        height="1.1rem"
                        icon="tabler:notes"
                        width="1.1rem"
                      />
                      <h4 className="text-accent-foreground font-semibold">
                        {t("finances:paymentsDetails.description", "Description")}
                      </h4>
                    </div>
                    <p className="text-muted-foreground text-sm">{payment.description}</p>
                  </div>
                )}
                {/* Confirmations moved to dialog */}
              </div>
            </Card>
          </motion.div>

          {/* History */}
          {history && history.length > 0 && (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
                <div className="absolute inset-0 opacity-8">
                  <div className="absolute -top-16 -left-16 h-36 w-36 rounded-full bg-indigo-400 to-purple-600" />
                  <div className="absolute -right-20 -bottom-20 h-44 w-44 rounded-full bg-cyan-400 to-green-500" />
                </div>
                <h3 className="text-accent-foreground mb-4 text-lg font-semibold">
                  {t("finances:paymentsDetails.historyTitle", "History")}
                </h3>
                <div className="relative">
                  <div className="space-y-4">
                    {history.map((entry, idx) => (
                      <motion.div
                        key={entry.id}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.25, delay: 0.05 * idx }}
                      >
                        <div className="bg-muted/20 rounded-lg p-4">
                          <p className="text-accent-foreground text-sm font-medium">
                            {entry.message}
                          </p>
                          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
                            {entry.initiator && (
                              <span className="text-accent-foreground/90">
                                {entry.initiator.full_name}
                              </span>
                            )}
                            <span>
                              {format(new Date(entry.created_at), "PPPp", {
                                locale: currentLanguageLocale(),
                              })}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Notifications moved next to payer card */}
        </div>
      </ScrollArea>
      <EditPaymentDialog
        current={{
          amount: details?.payment?.amount,
          currency: undefined,
          payment_due: details?.payment?.payment_due || undefined,
          description: details?.payment?.description || undefined,
          isCompleted: details?.payment?.status?.group === "completed",
        }}
        open={isEditDialogOpen}
        paymentId={id || null}
        onOpenChange={setIsEditDialogOpen}
        onUpdated={async () => fetchDetails()}
      />
      <NotifyPaymentDialog
        open={isNotifyDialogOpen}
        paymentId={id || null}
        onOpenChange={setIsNotifyDialogOpen}
        onSent={async () => fetchDetails()}
      />
      <ConfirmationReviewDialog
        allowResolveActions={!isArchived}
        confirmations={payment.confirmations}
        initialConfirmationId={selectedConfirmationId}
        open={isConfirmationDialogOpen}
        paymentId={id || null}
        onOpenChange={(o) => {
          if (!o) setSelectedConfirmationId(null);
          setIsConfirmationDialogOpen(o);
        }}
        onResolved={async () => fetchDetails()}
      />

      <DeletePaymentDialog
        open={isDeleteDialogOpen}
        paymentId={id || null}
        onDeleted={async () => fetchDetails()}
        onOpenChange={setIsDeleteDialogOpen}
      />

      <RestorePaymentDialog
        open={isRestoreDialogOpen}
        paymentId={id || null}
        onOpenChange={setIsRestoreDialogOpen}
        onRestored={async () => fetchDetails()}
      />
    </>
  );
}
